import { useMixpanel } from '@/context/mixpanel'
import type { ClaimResult } from '@/hooks/useCctpBridge'
import type { CctpMoneyInTransitItem } from '@/queries/cctpMoneyInTransit.query'
import { type CctpTransactionsResponse, useCctpTransactionsQuery } from '@/queries/cctpTransactions.query'
import { getNetworkName } from '@/utils/network/getNetworkName'
import type { Network } from '@vaultsfyi/common/types'
import { formatAmount } from '@vaultsfyi/common/utils'
import { type Address, formatUnits } from 'viem'
import { Button } from '../common/button'
import { ArrowExpandedIcon } from '../icons/arrow'
import { LoadingIcon } from '../icons/loading'

interface InTransitItemProps {
  item: CctpMoneyInTransitItem
  srcNetwork: Network
  dstNetwork: Network
  decimals: number
  account: Address | undefined
  executeClaim: (actionsData: CctpTransactionsResponse, dstNetwork: Network) => Promise<ClaimResult>
  isClaiming: boolean
}

export const InTransitItem = ({
  item,
  srcNetwork,
  dstNetwork,
  decimals,
  account,
  executeClaim,
  isClaiming,
}: InTransitItemProps) => {
  const { trackEvent } = useMixpanel()
  const { data: claimActions, isFetching } = useCctpTransactionsQuery({
    action: 'claim',
    userAddress: account,
    srcNetwork,
    dstNetwork,
    message: item.message,
    attestation: item.attestation,
    enabled: item.status === 'attested' && !!item.message && !!item.attestation,
  })

  const formattedAmount = formatAmount(Number(formatUnits(BigInt(item.amountNative), decimals)))

  const handleClaim = async () => {
    if (!claimActions) return
    const claimProps = { OriginNetwork: srcNetwork, DestinationNetwork: dstNetwork, Amount: `${formattedAmount} USDC` }
    trackEvent('CCTP claim initiated', claimProps)
    const result = await executeClaim(claimActions, dstNetwork)
    if (result.status === 'success') {
      trackEvent('CCTP claim successful', claimProps)
    } else if (result.status === 'error') {
      trackEvent('CCTP claim failed', { ...claimProps, Error: result.message })
    }
  }
  const isClaimable = item.status === 'attested' && !!claimActions

  return (
    <div className="flex items-center justify-between gap-2 rounded-2xl bg-surface px-3 py-2 text-sm">
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-heading-foreground">{formattedAmount} USDC</span>
        <div className="flex items-center gap-1">
          <span className="capitalize">{getNetworkName(srcNetwork)}</span>
          <ArrowExpandedIcon size={12} />
          <span className="capitalize">{getNetworkName(dstNetwork)}</span>
        </div>
        <span className="capitalize">{item.status}</span>
      </div>
      {isClaimable && (
        <Button variant="primary" size="sm" onClick={handleClaim} disabled={isClaiming || isFetching} type="button">
          {isFetching || isClaiming ? <LoadingIcon size={16} className="animate-spin" /> : 'Claim'}
        </Button>
      )}
      {item.status === 'pending' && <span className="text-xs">Waiting for attestation</span>}
    </div>
  )
}
